'use client';

import { useCallback, useMemo } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { HubNewsTagCategoryItem, HubNewsTagSelection } from '@/types/hubs';

const HUB_CASES_PATH = '/hubs/cases';
const HUB_QUERY_KEY = 'hub';

export const useHubNewsSearchParams = (categories: HubNewsTagCategoryItem[]) => {
    const router = useRouter();
    const searchParams = useSearchParams();

    const queryKey = searchParams.toString();
    const selectedHubIndex = searchParams.get(HUB_QUERY_KEY);

    const selection = useMemo(() => {
        const next: HubNewsTagSelection = {};
        if (selectedHubIndex) return next;
        categories.forEach((category) => {
            const tagCds = searchParams.get(category.cd);
            if (tagCds) next[category.cd] = tagCds.split(',').filter(Boolean);
        });
        return next;
    }, [categories, searchParams, selectedHubIndex]);

    const pushQuery = useCallback(
        (query: string) => {
            router.push(query ? `${HUB_CASES_PATH}?${query}` : HUB_CASES_PATH, { scroll: false });
        },
        [router],
    );

    const setSelection = useCallback(
        (next: HubNewsTagSelection) => {
            const params = new URLSearchParams();
            categories.forEach((category) => {
                const tagCds = next[category.cd] ?? [];
                if (tagCds.length > 0) params.set(category.cd, tagCds.join(','));
            });
            pushQuery(params.toString());
        },
        [categories, pushQuery],
    );

    const toggleTag = useCallback(
        (categoryCd: string, tagCd: string) => {
            const current = selection[categoryCd] ?? [];
            const next = current.includes(tagCd) ? current.filter((cd) => cd !== tagCd) : [...current, tagCd];
            setSelection({ ...selection, [categoryCd]: next });
        },
        [selection, setSelection],
    );

    const clearSelection = useCallback(() => setSelection({}), [setSelection]);

    const clearHub = useCallback(() => pushQuery(''), [pushQuery]);

    return {
        queryKey,
        selection,
        selectedHubIndex,
        setSelection,
        toggleTag,
        clearSelection,
        clearHub,
    };
};
